// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson
// The game table: opponents, the card in flight, your hand, and the compose tray.
import {
  type PublicState,
  type PublicSeat,
  type PrivateView,
  type CritterType,
} from './game';
import { critterSvg, cardBackSvg, CRITTER_NAME } from './critters';
import type { Sfx } from '@ben-gy/game-engine/sound';
import { shake, burst, stamp } from './fx';

export interface GameUiCallbacks {
  onPlay: (cardId: number, to: string, claim: CritterType) => void;
  onGuess: (g: 'truth' | 'bluff') => void;
  onPeekStart: () => void;
  onPeekPass: (to: string, claim: CritterType) => void;
  onPause: () => void;
  onFeedback: (el: HTMLElement) => void;
}

export interface GameMeta {
  modeName: string;
  round: number;
  tally: { name: string; wins: number; isSelf: boolean }[];
}

const esc = (s: string): string =>
  s.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]!);

/** Group a menagerie into critter chips (shared by the table and the results). */
function menagerieChipsFor(
  menagerie: PublicState['seats'][number]['menagerie'],
  threshold: number,
): string {
  const groups = new Map<CritterType, number>();
  for (const c of menagerie) groups.set(c.type, (groups.get(c.type) ?? 0) + 1);
  if (groups.size === 0) return '<span class="empty">clean slate</span>';
  return [...groups.entries()]
    .map(
      ([type, n]) =>
        `<span class="chip${n >= threshold ? ' dead' : n >= threshold - 1 ? ' danger' : ''}">${critterSvg(
          type,
          { size: 22 },
        )}<b>${n}</b></span>`,
    )
    .join('');
}

/**
 * The results body — EVERY player's outcome, not just yours (principle #9): the
 * final standings, each player's full menagerie, and what put the loser out.
 */
export function resultsBodyHtml(
  pub: PublicState,
  selfId: string,
  meta: GameMeta,
): string {
  const byId = new Map(pub.seats.map((s) => [s.id, s]));
  const rank = pub.ranking.length ? pub.ranking : pub.seats.map((s) => s.id);
  const winner = byId.get(rank[0]);
  const iLost = pub.loserId === selfId;
  const iWon = rank[0] === selfId;
  const head = iWon
    ? 'You survived! 🏆'
    : iLost
      ? 'You collected one too many.'
      : `${esc(winner?.name ?? '')} survives.`;
  const rows = rank
    .map((id, i) => {
      const s = byId.get(id)!;
      const isLoser = id === pub.loserId;
      const isSelf = id === selfId;
      const place = isLoser ? 'OUT' : `#${i + 1}`;
      return `<div class="res-row${isLoser ? ' loser' : ''}${isSelf ? ' self' : ''}${i === 0 && !isLoser ? ' winner' : ''}">
        <span class="res-place">${place}</span>
        <span class="res-name">${esc(s.name)}${isSelf ? ' (you)' : ''}</span>
        <span class="res-men">${menagerieChipsFor(s.menagerie, pub.rules.threshold)}</span>
      </div>`;
    })
    .join('');
  const tally =
    meta.tally.length > 1
      ? `<div class="res-tally">Match: ${meta.tally
          .map((t) => `${esc(t.name)} ${t.wins}`)
          .join(' · ')}</div>`
      : '';
  return `<h2 class="res-head">${head}</h2>${tally}<div class="res-rows">${rows}</div>`;
}

export class GameUi {
  private root: HTMLElement;
  private selfId: string;
  private sfx: Sfx;
  private cb: GameUiCallbacks;
  private pub: PublicState | null = null;
  private priv: PrivateView | null = null;
  private meta: GameMeta = { modeName: '', round: 1, tally: [] };

  // compose state
  private selCard: number | null = null;
  private selTarget: string | null = null;

  private lastLogLen = 0;
  private lastResolveKey = '';
  private onClick: (e: Event) => void;

  constructor(root: HTMLElement, selfId: string, sfx: Sfx, cb: GameUiCallbacks) {
    this.root = root;
    this.selfId = selfId;
    this.sfx = sfx;
    this.cb = cb;
    this.onClick = (e) => this.handle(e);
    this.root.addEventListener('click', this.onClick);
  }

  destroy(): void {
    this.root.removeEventListener('click', this.onClick);
    this.root.innerHTML = '';
  }

  update(pub: PublicState, priv: PrivateView | null, meta: GameMeta): void {
    const firstPaint = this.pub === null;
    this.pub = pub;
    this.priv = priv;
    this.meta = meta;
    this.juice(firstPaint);
    this.render();
  }

  // ---- juice: react to new log entries + reveals ---------------------------

  private juice(firstPaint: boolean): void {
    const pub = this.pub!;
    if (firstPaint) {
      this.lastLogLen = pub.log.length;
      this.lastResolveKey = pub.lastResolve ? `${pub.lastResolve.cardId}:${pub.ply}` : '';
      return;
    }
    for (let i = this.lastLogLen; i < pub.log.length; i++) {
      const kind = pub.log[i].kind;
      if (kind === 'pass' || kind === 'peek') this.sfx.play('blip', { pitch: 0.9 });
      if (kind === 'danger') this.sfx.play('powerup', { pitch: 0.6, gain: 0.7 });
    }
    this.lastLogLen = pub.log.length;

    const r = pub.lastResolve;
    const key = r ? `${r.cardId}:${pub.ply}` : '';
    if (r && key !== this.lastResolveKey) {
      this.lastResolveKey = key;
      const good = r.receiverCorrect;
      const iCaught = r.receiver === this.selfId && good;
      const iFooled = r.receiver === this.selfId && !good;
      this.sfx.play(good ? 'coin' : 'hit', { pitch: good ? 1 : 0.8 });
      if ('vibrate' in navigator) try { navigator.vibrate(good ? 20 : 40); } catch { /* ignore */ }
      const stage = this.root.querySelector('.stage') as HTMLElement | null;
      if (stage) {
        const rect = stage.getBoundingClientRect();
        burst(this.root, rect.left + rect.width / 2, rect.top + rect.height / 2, r.trueType, iCaught || iFooled);
        if (iCaught) stamp(stage, 'CAUGHT!', true);
        else if (iFooled) stamp(stage, 'FOOLED!', false);
      }
      if (iFooled) shake(this.root, 1);
    }
    if (pub.phase === 'over') {
      this.sfx.play(pub.loserId === this.selfId ? 'lose' : 'win');
      if ('vibrate' in navigator) try { navigator.vibrate(pub.loserId === this.selfId ? [60, 40, 60] : 30); } catch { /* ignore */ }
    }
  }

  // ---- render --------------------------------------------------------------

  private seat(id: string): PublicSeat | undefined {
    return this.pub!.seats.find((s) => s.id === id);
  }

  private actorId(): string {
    const f = this.pub!.inFlight;
    return f ? f.peeking ?? f.to : this.pub!.turn;
  }

  private render(): void {
    const pub = this.pub!;
    const opponents = pub.seats.filter((s) => s.id !== this.selfId);
    this.root.innerHTML = `
      <div class="hud">
        <button class="icon-btn" data-act="pause" aria-label="Pause">☰</button>
        <div class="hud-mid"><span class="hud-mode">${esc(this.meta.modeName)}</span>
          <span class="hud-round">Round ${this.meta.round}</span></div>
        <div class="hud-tally">${this.tallyHtml()}</div>
      </div>
      <div class="opponents c${opponents.length}">
        ${opponents.map((s) => this.playerHtml(s)).join('')}
      </div>
      <div class="stage">${this.stageHtml()}</div>
      <div class="you">
        ${this.youMenagerieHtml()}
        <div class="tray">${this.trayHtml()}</div>
        ${this.handHtml()}
      </div>`;
  }

  private tallyHtml(): string {
    if (this.meta.tally.length === 0) return '';
    return this.meta.tally
      .map((t) => `<span class="tchip${t.isSelf ? ' self' : ''}">${esc(t.name)} ${t.wins}</span>`)
      .join('');
  }

  private threat(count: number): string {
    const thr = this.pub!.rules.threshold;
    if (count >= thr - 1) return ' danger';
    if (count >= thr - 2 && thr >= 4) return ' warn';
    return '';
  }

  private menagerieChips(s: PublicSeat): string {
    const groups = new Map<CritterType, number>();
    for (const c of s.menagerie) groups.set(c.type, (groups.get(c.type) ?? 0) + 1);
    if (groups.size === 0) return '<span class="empty">—</span>';
    return [...groups.entries()]
      .map(
        ([type, n]) =>
          `<span class="chip${this.threat(n)}" title="${CRITTER_NAME[type]} ×${n}">${critterSvg(type, {
            size: 22,
          })}<b>${n}</b></span>`,
      )
      .join('');
  }

  private tellHtml(s: PublicSeat): string {
    const total = s.tell.truths + s.tell.lies;
    if (total === 0) return '<span class="tell none">no reads yet</span>';
    const lieRate = Math.round((100 * s.tell.lies) / total);
    return `<span class="tell" title="Of ${total} revealed claims, ${s.tell.lies} were lies">🃏 ${s.tell.lies} lie${s.tell.lies === 1 ? '' : 's'} · ${lieRate}%</span>`;
  }

  private playerHtml(s: PublicSeat): string {
    const isActor = this.actorId() === s.id && this.pub!.phase === 'play';
    const peeking = this.pub!.inFlight?.peeking === this.selfId;
    const isTargetable = this.canTarget(s.id) && (peeking || this.selCard != null);
    const cls = [
      'player',
      s.alive ? '' : 'dead',
      isActor ? 'actor' : '',
      isTargetable ? 'targetable' : '',
    ]
      .filter(Boolean)
      .join(' ');
    const peekBadge =
      this.pub!.rules.peek && s.alive ? `<span class="peeks" title="peeks left">◔ ${s.peeksLeft}</span>` : '';
    return `<div class="${cls}" ${isTargetable ? `data-act="target" data-id="${s.id}"` : ''}>
      <div class="p-top"><span class="p-name">${esc(s.name)}</span>${peekBadge}</div>
      <div class="p-hand">${s.alive ? cardBackSvg(16) + `<span>${s.handCount}</span>` : '<span class="out-tag">OUT</span>'}</div>
      <div class="p-men">${this.menagerieChips(s)}</div>
      <div class="p-tell">${this.tellHtml(s)}</div>
    </div>`;
  }

  private canTarget(id: string): boolean {
    // Give phase: any alive opponent. Peek phase: any alive non-trail player.
    const pub = this.pub!;
    const s = this.seat(id);
    if (!s || !s.alive || id === this.selfId) return false;
    if (pub.inFlight?.peeking === this.selfId) {
      return !pub.inFlight.trail.includes(id) && id !== pub.inFlight.to;
    }
    return this.actorId() === this.selfId && !pub.inFlight;
  }

  private stageHtml(): string {
    const pub = this.pub!;
    const r = pub.lastResolve;
    if (pub.phase === 'play' && pub.inFlight) {
      const f = pub.inFlight;
      const fromN = this.seat(f.from)?.name ?? '?';
      const toN = this.seat(f.to)?.name ?? '?';
      const iSee = this.priv?.inFlightTrueType ?? null;
      const face =
        iSee != null
          ? `<div class="flightcard seen">${critterSvg(iSee, { size: 64 })}<span class="really">you saw: ${CRITTER_NAME[iSee]}</span></div>`
          : `<div class="flightcard">${cardBackSvg(56)}</div>`;
      const trail = f.trail.length > 1 ? `<div class="trail">passed ${f.trail.length - 1}×</div>` : '';
      return `${face}
        <div class="claim-line"><b>${esc(fromN)}</b> → <b>${esc(toN)}</b><br><span class="claim">“a ${CRITTER_NAME[f.claim]}”</span></div>${trail}`;
    }
    if (r) {
      const takerLine =
        r.taker === this.selfId ? 'You take it.' : `${esc(this.seat(r.taker)?.name ?? '?')} takes it.`;
      return `<div class="flightcard reveal">${critterSvg(r.trueType, { size: 64 })}</div>
        <div class="claim-line">It was a <b>${CRITTER_NAME[r.trueType]}</b> — claim was “${CRITTER_NAME[r.claim]}”.<br>
        <span class="taker">${takerLine}</span></div>`;
    }
    return `<div class="stage-idle">A new round of nerve.</div>`;
  }

  private youMenagerieHtml(): string {
    const me = this.seat(this.selfId);
    if (!me) return '<div class="you-men spectator">You’re spectating this round.</div>';
    return `<div class="you-men">
      <span class="you-label">${esc(me.name)} (you)</span>
      <span class="you-chips">${this.menagerieChips(me)}</span>
      ${this.pub!.rules.peek ? `<span class="you-peeks">◔ ${me.peeksLeft} peek${me.peeksLeft === 1 ? '' : 's'}</span>` : ''}
    </div>`;
  }

  private trayHtml(): string {
    const pub = this.pub!;
    if (pub.phase === 'over') return '';
    const me = this.seat(this.selfId);
    if (!me || !me.alive) return `<div class="tray-msg">Out this round — watching the rest.</div>`;

    const f = pub.inFlight;
    // I'm peeking: I've seen the card, now pass it on.
    if (f && f.peeking === this.selfId) {
      const t = this.priv?.inFlightTrueType;
      if (this.selTarget == null) {
        return `<div class="tray-title">It’s really a <b>${t ? CRITTER_NAME[t] : '?'}</b>. Pass it to…</div>
          <div class="tray-hint">tap a player above</div>`;
      }
      return `<div class="tray-title">Passing to <b>${esc(this.seat(this.selTarget)?.name ?? '')}</b> — say it’s a…</div>
        ${this.claimButtons()}<button class="btn ghost" data-act="cancel">back</button>`;
    }
    // I'm the receiver: believe / bluff / peek.
    if (f && f.to === this.selfId) {
      const fromN = this.seat(f.from)?.name ?? '?';
      const canPeek = pub.rules.peek && me.peeksLeft > 0 && this.peekTargetsExist();
      return `<div class="tray-title"><b>${esc(fromN)}</b> says it’s a <b>${CRITTER_NAME[f.claim]}</b>. Do you buy it?</div>
        <div class="calls">
          <button class="btn believe" data-act="guess" data-g="truth">Believe</button>
          <button class="btn bluff" data-act="guess" data-g="bluff">Call bluff</button>
          ${canPeek ? `<button class="btn peek" data-act="peek">Peek &amp; pass</button>` : ''}
        </div>`;
    }
    // My turn to give.
    if (!f && pub.turn === this.selfId) {
      if (this.selCard == null) return `<div class="tray-title">Your turn — tap a card to pass.</div>`;
      if (this.selTarget == null)
        return `<div class="tray-title">Pass to… <span class="tray-hint">tap a player above</span></div>
          <button class="btn ghost" data-act="cancel">back</button>`;
      return `<div class="tray-title">To <b>${esc(this.seat(this.selTarget)?.name ?? '')}</b> — say it’s a…</div>
        ${this.claimButtons()}<button class="btn ghost" data-act="cancel">back</button>`;
    }
    // Waiting.
    const actorN = this.seat(this.actorId())?.name ?? '…';
    return `<div class="tray-msg">Waiting for <b>${esc(actorN)}</b>…</div>`;
  }

  private claimButtons(): string {
    return `<div class="claims-pick">${this.pub!.rules.types
      .map(
        (t) =>
          `<button class="claim-btn" data-act="claim" data-type="${t}" aria-label="${CRITTER_NAME[t]}">${critterSvg(
            t,
            { size: 30 },
          )}<span>${CRITTER_NAME[t]}</span></button>`,
      )
      .join('')}</div>`;
  }

  private peekTargetsExist(): boolean {
    const pub = this.pub!;
    if (!pub.inFlight) return false;
    return pub.seats.some(
      (s) => s.alive && s.id !== this.selfId && !pub.inFlight!.trail.includes(s.id),
    );
  }

  private handHtml(): string {
    const hand = this.priv?.hand ?? [];
    const myTurn = this.pub!.phase === 'play' && !this.pub!.inFlight && this.pub!.turn === this.selfId;
    if (hand.length === 0) return `<div class="hand empty">no cards</div>`;
    return `<div class="hand">${hand
      .map(
        (c) =>
          `<button class="handcard${this.selCard === c.id ? ' sel' : ''}${myTurn ? ' live' : ''}"
            ${myTurn ? `data-act="pickcard" data-id="${c.id}"` : 'disabled'}
            aria-label="${CRITTER_NAME[c.type]}">${critterSvg(c.type, { size: 40 })}</button>`,
      )
      .join('')}</div>`;
  }

  // ---- events --------------------------------------------------------------

  private handle(e: Event): void {
    const el = (e.target as HTMLElement).closest('[data-act]') as HTMLElement | null;
    if (!el) return;
    const act = el.dataset.act;
    switch (act) {
      case 'pause':
        this.cb.onPause();
        break;
      case 'feedback':
        this.cb.onFeedback(el);
        break;
      case 'pickcard':
        this.selCard = Number(el.dataset.id);
        this.selTarget = null;
        this.sfx.play('select');
        this.render();
        break;
      case 'target':
        this.selTarget = el.dataset.id!;
        this.sfx.play('select');
        this.render();
        break;
      case 'claim': {
        const claim = el.dataset.type as CritterType;
        if (this.pub?.inFlight?.peeking === this.selfId && this.selTarget) {
          this.cb.onPeekPass(this.selTarget, claim);
        } else if (this.selCard != null && this.selTarget) {
          this.cb.onPlay(this.selCard, this.selTarget, claim);
        }
        this.selCard = null;
        this.selTarget = null;
        break;
      }
      case 'cancel':
        if (this.selTarget) this.selTarget = null;
        else this.selCard = null;
        this.render();
        break;
      case 'guess':
        this.cb.onGuess(el.dataset.g === 'bluff' ? 'bluff' : 'truth');
        break;
      case 'peek':
        this.selTarget = null;
        this.cb.onPeekStart();
        break;
    }
  }
}
